import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'secondarySubtle'
    | 'ghost'
    | 'outline'
    | 'destructive'
    | 'destructiveOutline'
    | 'neutralOutline'
    | 'tertiary';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variantStyles: Record<string, string> = {
  primary:
    'bg-primary text-primary-foreground border border-primary-hover hover:bg-primary-hover active:bg-primary-active',
  secondary:
    'bg-white text-muted-foreground border border-border-strong hover:bg-surface-secondary',
  // `secondary` on the lighter `border/neutral/default-subtle` #e5e5e5 —
  // the inline "Add" beside the sender search (QA on YAR-161, Oct 5).
  secondarySubtle:
    'bg-white text-muted-foreground border border-border hover:bg-surface-secondary',
  ghost: 'bg-transparent text-primary hover:bg-primary-light',
  outline:
    'bg-white border border-primary-hover text-primary hover:bg-primary-light',
  destructive:
    'bg-danger text-surface border border-danger hover:bg-danger-hover',
  // The outline counterpart of `destructive`: white fill, error-toned border
  // and label, both `#a82222` (Figma `border/error/default` +
  // `text/error/default`). Destructive confirmations in a modal are drawn
  // this way rather than as a solid red slab — see the decline-invite modal
  // (Figma 9704:112731). Kept separate from `destructive` so the solid fill
  // used elsewhere is untouched.
  destructiveOutline:
    'bg-white text-text-error border border-border-error hover:bg-danger-light',
  // Grey outline for neutral recovery actions — label and border both
  // #5e5e5e (the onboarding "Try again", QA on YAR-160), so a retry doesn't
  // read as the screen's primary action.
  neutralOutline:
    'bg-white text-muted-foreground border border-muted-foreground hover:bg-surface-secondary',
  tertiary:
    'bg-transparent text-muted-foreground hover:bg-surface-secondary active:bg-surface-secondary',
};

const sizeStyles: Record<string, string> = {
  sm: 'px-1.5 py-1 text-sm leading-5 gap-1.5',
  md: 'px-2 py-1.5 text-base leading-6 gap-1.5',
  lg: 'px-2.5 py-2 text-lg leading-7 gap-1.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        data-variant={variant}
        disabled={disabled || loading}
        className={[
          'inline-flex items-center justify-center font-semibold rounded-sm cursor-pointer',
          'transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          'disabled:bg-disabled-bg disabled:text-disabled-text disabled:border-transparent disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth ? 'w-full' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {loading ? (
          <Loader2
            className="animate-spin"
            size={size === 'sm' ? 16 : size === 'md' ? 16 : 24}
          />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';
